// JOBE-0166.3: metricStatus — threshold evaluation for per-metric StatusPill.
//
// Returns { tone, label } for a metric.
//   tone:  'ok' | 'warn' | 'bad' | 'pencil'
//   label: 'PASS' | 'WARN' | 'FAIL' | 'NOT RUN'
//
// Lower-is-better: unit === 'ms' is always lower-is-better. A fixed set of
// IDs (pollution, contamination rates) are also lower-is-better regardless
// of unit.
//
// Per-metric margins: derived from the implicit thresholds in EvalMetricsTab.jsx
// badge() function (JOBE-0076.7.3) plus new entries for the fixture suites
// added in JOBE-0166.1.

/**
 * Metric IDs that are lower-is-better (independent of unit).
 * All unit='ms' metrics are also lower-is-better (handled inline).
 */
const LOWER_IS_BETTER = new Set([
  'pollution_rate',
  'stale_recall_rate',
  'cross_agent_contamination_rate',
  'fr-contamination',
  'fc-pollution',
  'fr-error-rate',
]);

/**
 * Per-metric warn margins (in native unit).
 * If a metric ID is not present, defaultMargin() is used.
 */
const METRIC_MARGINS = {
  // ratio metrics — margin is a fraction
  write_precision:                0.02,
  write_recall:                   0.02,
  retrieval_precision_at_k:       0.03,
  retrieval_recall_at_k:          0.03,
  mrr_target_memories:            0.03,
  stale_recall_rate:              0.05,   // warn window: target + 0.05 = 0.15
  pollution_rate:                 0.02,   // warn window: 0 + 0.02 = 0.02
  conflict_surfacing_rate:        0.05,
  provenance_completeness:        0.04,
  deletion_completeness:          0.00,  // zero tolerance
  cross_agent_contamination_rate: 0.00,  // zero tolerance
  context_usefulness:             0.05,
  // latency metrics — margin in ms
  latency_token_cost:             30000, // 30s warn window above 2-min target
  'fc-p50-latency':               120,
  'fc-p95-latency':               400,
  'cr-p50-latency':               500,
  'rq-retrieval-latency':         80,
  'fr-p50-latency':               140,
  'fr-p95-latency':               600,
  // error rate
  'fr-error-rate':                0.02,
  // finance core
  'fc-write-precision':           0.02,
  'fc-write-recall':              0.02,
  'fc-currency-parse':            0.03,
  'fc-provenance':                0.04,
  'fc-pollution':                 0.02,
  // code review v2
  'cr-precision':                 0.03,
  'cr-recall':                    0.03,
  'cr-context-precision':         0.03,
  'cr-mrr':                       0.03,
  'cr-throughput':                2,
  // research qa
  'rq-precision-at-k':            0.03,
  'rq-recall-at-k':               0.03,
  'rq-mrr':                       0.03,
  'rq-citation':                  0.05,
  // fleet regression
  'fr-fleet-precision':           0.02,
  'fr-fleet-recall':              0.02,
  'fr-context-useful':            0.05,
  'fr-contamination':             0.00,
  'fr-deletion':                  0.00,
};

/**
 * Default warn margin when the metric ID is not in METRIC_MARGINS.
 * @param {number} target
 * @param {'ratio'|'ms'|'count'} unit
 * @returns {number}
 */
function defaultMargin(target, unit) {
  if (unit === 'ms') return target * 0.15;    // 15% of target
  if (unit === 'ratio') return 0.03;
  return 0;
}

/**
 * Evaluate a metric against its threshold and return the pill tone + label.
 * @param {{ id: string, current: number|null, target: number, unit: 'ratio'|'ms'|'count' }} metric
 * @returns {{ tone: 'ok'|'warn'|'bad'|'pencil', label: 'PASS'|'WARN'|'FAIL'|'NOT RUN' }}
 */
export function metricStatus(metric) {
  const { current, target, unit } = metric;

  if (current === null || current === undefined) {
    return { tone: 'pencil', label: 'NOT RUN' };
  }

  const lowerIsBetter = LOWER_IS_BETTER.has(metric.id) || unit === 'ms';
  const margin = metric.id in METRIC_MARGINS
    ? METRIC_MARGINS[metric.id]
    : defaultMargin(target, unit);

  let delta;
  if (lowerIsBetter) {
    // Pass when current <= target; fail when current > target + margin.
    delta = current - target;
  } else {
    // Pass when current >= target; fail when current < target - margin.
    delta = target - current;
  }

  if (delta <= 0) return { tone: 'ok',   label: 'PASS' };
  if (delta <= margin) return { tone: 'warn', label: 'WARN' };
  return { tone: 'bad', label: 'FAIL' };
}
